/**
 * onboardingAnalytics.js — Shared event tracking utility for onboarding funnel
 * Pushes to window.dataLayer (GA4) and logs in development.
 * Extensible for PostHog/Amplitude later.
 */

const isDev = process.env.NODE_ENV === 'development';

function trackEvent(eventName, properties = {}) {
  const payload = { event: eventName, ...properties, timestamp: new Date().toISOString() };

  // GA4 dataLayer
  if (window.dataLayer) {
    window.dataLayer.push(payload);
  }

  if (isDev) {
    console.log(`[Onboarding] ${eventName}`, properties);
  }
}

// Wizard lifecycle
export const trackWizardStart = (source = 'command_center') =>
  trackEvent('onboarding_wizard_start', { source });

export const trackWizardStep = (step, stepName) =>
  trackEvent('onboarding_wizard_step', { step, stepName });

export const trackWizardSkip = (step, skipType = 'skip_all') =>
  trackEvent('onboarding_wizard_skip', { step, skipType });

export const trackWizardComplete = (hasAsset, hasScanResult, findingsCount) =>
  trackEvent('onboarding_wizard_complete', { hasAsset, hasScanResult, findingsCount });

export const trackWizardReopen = (source = 'settings') =>
  trackEvent('onboarding_wizard_reopen', { source });

// Asset & scan
export const trackAssetAdded = (assetType, assetId) =>
  trackEvent('onboarding_asset_added', { assetType, assetId });

export const trackScanStart = (target, scanId) =>
  trackEvent('onboarding_scan_start', { target, scanId });

export const trackScanComplete = (scanId, subdomains, ports, findings, riskScore, duration_ms) =>
  trackEvent('onboarding_scan_complete', { scanId, subdomains, ports, findings, riskScore, duration_ms });

export const trackScanError = (error_message) =>
  trackEvent('onboarding_scan_error', { error_message });

// CTA clicks
export const trackViewResults = (scanId) =>
  trackEvent('onboarding_view_results', { scanId });

export const trackCtaClick = (cta) =>
  trackEvent('onboarding_cta_click', { cta });

// First-scan banner
export const trackFirstScanBannerShown = (scanId) =>
  trackEvent('first_scan_banner_shown', { scanId });

export const trackFirstScanBannerCta = (action) =>
  trackEvent('first_scan_banner_cta', { action });

// WAF blocking banner (PRD-56)
export const trackWafBannerShown = (assetId, vendor, band) =>
  trackEvent('waf_banner_shown', { assetId, vendor, band });

export const trackWafBannerCta = (action, assetId) =>
  trackEvent('waf_banner_cta', { action, assetId });

// Coverage banner (PRD-65 Phase 3) — fires when tier cap dropped >50 URLs AND >=20% of total.
// targetTier is the next-meaningful tier (skips same-cap, so Trial→Pro not Trial→Standard).
export const trackCoverageBannerShown = (assetId, currentTier, targetTier, fractionBucket) =>
  trackEvent('coverage_banner_shown', { assetId, currentTier, targetTier, fractionBucket });

export const trackCoverageBannerCta = (action, assetId, currentTier, targetTier) =>
  trackEvent('coverage_banner_cta', { action, assetId, currentTier, targetTier });
